import { MENTORING_SUBSCRIPTION_PERIOD_COUNT } from '@algoacademy/backend/modules/account/mentoring-prices'
import {
  calculateDiscountedInvoiceGrosze,
  type MentoringDiscount,
  MINIMUM_INVOICE_GROSZE,
  planDiscountPhases
} from '@algoacademy/backend/modules/payment/discount-pricing'
import { PAYMENT__DISCOUNT_SHAPE } from '@algoacademy/backend/modules/payment/enums'
import type { MentoringOfferProduct } from '../../../_forms/MentoringOfferForm/_helpers/mentoring-terms'
import { roundUpToNearestZloty } from '../../../prices'
import { readDiscountPreviewFigures } from './discount-preview'

const GROSZE_PER_ZLOTY = 100

/**
 * What the discount adds up to. A discount with an end has a total to state; one
 * that never ends has none, so it says how much lighter each billing period and
 * each 4-week slice is instead.
 */
export type MentoringDiscountTotals =
  | {
      kind: 'ending'
      /** Billing periods (invoices) the discount covers, as staff typed it. */
      periodCount: number
      invoiceCount: number
      chargedZloty: number
      listZloty: number
      savedZloty: number
    }
  | {
      kind: 'endless'
      savedPerBillingPeriodZloty: number
      /** Staff savings on the card, spread to one 4-week slice, rounded up. */
      savedPerFourWeeksZloty: number
    }

export type MentoringDiscountLadder = {
  /** One 4-week period while the discount runs - the figure the buyer actually pays. */
  chargedPerFourWeeksZloty: number
  /** The invoice the cadence bills before staff touches anything. */
  listInvoiceZloty: number
  /** The first invoice with this discount on it. */
  discountedInvoiceZloty: number
  totals: MentoringDiscountTotals
}

function readDiscountTotals(args: {
  listInvoiceGrosze: number
  discountedInvoiceGrosze: number
  periodsPerInvoice: number
  discount: MentoringDiscount
}): MentoringDiscountTotals {
  const { listInvoiceGrosze, discountedInvoiceGrosze, periodsPerInvoice, discount } = args
  const savedPerInvoiceGrosze = listInvoiceGrosze - discountedInvoiceGrosze

  if (discount.periodCount === null) {
    const isAtMinimumInvoice = discountedInvoiceGrosze === MINIMUM_INVOICE_GROSZE
    const nominalBillingPeriodZloty = discount.value / GROSZE_PER_ZLOTY

    if (discount.shape === PAYMENT__DISCOUNT_SHAPE.AMOUNT && !isAtMinimumInvoice) {
      return {
        kind: 'endless',
        savedPerBillingPeriodZloty: nominalBillingPeriodZloty,
        savedPerFourWeeksZloty: roundUpToNearestZloty(nominalBillingPeriodZloty / periodsPerInvoice)
      }
    }

    const savedPerBillingPeriodZloty = savedPerInvoiceGrosze / GROSZE_PER_ZLOTY

    return {
      kind: 'endless',
      savedPerBillingPeriodZloty,
      savedPerFourWeeksZloty: roundUpToNearestZloty(savedPerBillingPeriodZloty / periodsPerInvoice)
    }
  }

  const { phases } = planDiscountPhases({ listInvoiceGrosze, discount })

  const invoiceCount = discount.periodCount
  const chargedGrosze = phases.reduce(
    (total, phase) => total + phase.amountInGrosze * phase.iterations,
    0
  )
  const listGrosze = listInvoiceGrosze * invoiceCount

  return {
    kind: 'ending',
    periodCount: discount.periodCount,
    invoiceCount,
    chargedZloty: chargedGrosze / GROSZE_PER_ZLOTY,
    listZloty: listGrosze / GROSZE_PER_ZLOTY,
    savedZloty: (listGrosze - chargedGrosze) / GROSZE_PER_ZLOTY
  }
}

/**
 * The price ladder the discount screen shows above the form, and what it all adds
 * up to. Every figure is the panel's own figure or comes from the pricing module
 * the checkout uses, so the card cannot quote a price the buyer will not be
 * charged, nor a złoty the panel below disagrees with.
 */
export function readDiscountLadder(args: {
  mentoringProduct: MentoringOfferProduct
  discount: MentoringDiscount
}): MentoringDiscountLadder {
  const { mentoringProduct, discount } = args

  const figures = readDiscountPreviewFigures({ mentoringProduct, discount })
  const periodsPerInvoice = MENTORING_SUBSCRIPTION_PERIOD_COUNT[mentoringProduct.subscription]

  const runningInvoiceGrosze = calculateDiscountedInvoiceGrosze({
    listInvoiceGrosze: figures.listInvoiceGrosze,
    discount: { ...discount, periodCount: null }
  })

  return {
    chargedPerFourWeeksZloty: roundUpToNearestZloty(
      runningInvoiceGrosze / periodsPerInvoice / GROSZE_PER_ZLOTY
    ),
    listInvoiceZloty: figures.listInvoiceGrosze / GROSZE_PER_ZLOTY,
    discountedInvoiceZloty: figures.discountedInvoiceGrosze / GROSZE_PER_ZLOTY,
    totals: readDiscountTotals({
      listInvoiceGrosze: figures.listInvoiceGrosze,
      discountedInvoiceGrosze: figures.discountedInvoiceGrosze,
      periodsPerInvoice,
      discount
    })
  }
}
